import { useState } from 'react'
import { LoaderFunctionArgs, useLoaderData, useRevalidator } from 'react-router-dom'
import { useCurrentHealthCenter, useIsAccountClosed } from '../../components/AppShell'
import { Alert, ButtonAnchor, ErrorList, Modal, SubmitButton, TextField, useConfirm } from '../../components/common'
import {
  ProfessionalCalendar,
  createScheduleInterval,
  deleteScheduleInterval,
  fetchProfessionalCalendar,
  updateScheduleInterval,
} from '../../api/calendar'
import { ApiError } from '../../lib/api'
import { trackEvent } from '../../lib/config'
import { useImpression } from '../../lib/useImpression'
import styles from './ProfessionalCalendarPage.module.scss'

const WEEK_DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']
const DAY_LABELS: Record<string, string> = {
  Monday: 'Lunes',
  Tuesday: 'Martes',
  Wednesday: 'Miércoles',
  Thursday: 'Jueves',
  Friday: 'Viernes',
  Saturday: 'Sábado',
  Sunday: 'Domingo',
}

type ModalState = { day: string; id: number | null; startAt: string; endAt: string }

const SCHEDULE_STEP_MINUTES = 15

const snapToStep = (time: string): string => {
  const [hours, minutes] = time.split(':').map(Number)
  if (Number.isNaN(hours) || Number.isNaN(minutes)) {
    return time
  }
  const snapped = Math.round(minutes / SCHEDULE_STEP_MINUTES) * SCHEDULE_STEP_MINUTES
  const carry = Math.floor(snapped / 60)
  return `${String((hours + carry) % 24).padStart(2, '0')}:${String(snapped % 60).padStart(2, '0')}`
}

export function loader({ params }: LoaderFunctionArgs): Promise<ProfessionalCalendar> {
  return fetchProfessionalCalendar(Number(params.healthCenterId), params.professionalId as string)
}

export default function ProfessionalCalendarPage() {
  const healthCenter = useCurrentHealthCenter()
  const closed = useIsAccountClosed()
  const confirm = useConfirm()
  const calendar = useLoaderData() as Awaited<ReturnType<typeof loader>>
  const { revalidate } = useRevalidator()
  const [modal, setModal] = useState<ModalState | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  const showEmptyScheduleHint = Object.keys(calendar.schedule).length === 0
  useImpression('Onboarding Hint Shown', showEmptyScheduleHint ? { hint: 'empty_schedule' } : null)

  if (!healthCenter) {
    return null
  }

  const schedule = calendar.schedule
  const professional = calendar.professional

  const handleSave = async () => {
    if (!modal || submitting) {
      return
    }
    setSubmitting(true)
    setError(null)
    const startAt = snapToStep(modal.startAt)
    const endAt = snapToStep(modal.endAt)
    try {
      if (modal.id === null) {
        await createScheduleInterval(healthCenter.id, professional.id, modal.day, startAt, endAt)
      } else {
        await updateScheduleInterval(healthCenter.id, professional.id, modal.day, modal.id, startAt, endAt)
      }
      trackEvent('Schedule Interval Saved', { day: modal.day, is_new: modal.id === null })
      setModal(null)
      await revalidate()
    } catch (requestError) {
      const body = requestError instanceof ApiError ? (requestError.body as { errors?: string } | null) : null
      trackEvent('Form Save Failed', {
        form: 'schedule_interval',
        reason: body?.errors ? 'validation' : 'request',
        status_code: requestError instanceof ApiError ? requestError.status : undefined,
        server_message: body?.errors,
      })
      setError(body?.errors ?? 'Ocurrió un error, inténtalo nuevamente')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (day: string, id: number) => {
    const confirmed = await confirm({ title: 'Eliminar horario', message: '¿Estás seguro de que deseas eliminar este horario?' })
    if (!confirmed) {
      return
    }
    setError(null)
    try {
      await deleteScheduleInterval(healthCenter.id, professional.id, day, id)
      trackEvent('Schedule Interval Deleted', { day })
      await revalidate()
    } catch (requestError) {
      const body = requestError instanceof ApiError ? (requestError.body as { errors?: string } | null) : null
      setError(body?.errors ?? 'Ocurrió un error, inténtalo nuevamente')
    }
  }

  return (
    <>
      <div className="col-xs-12">
        <ErrorList messages={error ? [error] : []} />
        <h3 className="text-center blue-subtitle">Horario de trabajo para {professional.name}</h3>
        {professional.rut ? <h4 className="text-center blue-subtitle">{professional.rut}</h4> : null}
      </div>
      <div className="col-xs-12 col-md-8 panel col-md-offset-2">
        {showEmptyScheduleHint && (
          <div className="row">
            <Alert kind="info" centered>
              Aquí debes agregar los horarios en que el profesional puede atender pacientes
              <br />
              Para agregar un horario haz click en el botón + abajo del día que corresponda
            </Alert>
          </div>
        )}
        <div className="col-xs-12">
          <div className={`row ${styles.sevenCols}`} style={{ marginBottom: 40 }}>
            {WEEK_DAYS.map((day) => {
              const intervals = schedule[day] ?? []
              return (
                <div key={day} className="col-xs-7 col-sm-1 text-center">
                  <h4>
                    {DAY_LABELS[day]}
                    <br />
                    {!closed && (
                      <ButtonAnchor
                        href="#"
                        className={styles.addButton}
                        onClick={(clickEvent) => {
                          clickEvent.preventDefault()
                          setError(null)
                          trackEvent('Schedule Interval Modal Opened', { is_new: true })
                          setModal({ day, id: null, startAt: '08:00', endAt: '13:00' })
                        }}
                      >
                        +
                      </ButtonAnchor>
                    )}
                  </h4>
                  <div className="row">
                    <ul className="list-group">
                      {intervals.length === 0 ? (
                        <span className="na">Vacío</span>
                      ) : (
                        intervals.map((interval, index) => (
                          <li key={index} className="list-group-item">
                            {closed ? (
                              <span>
                                {interval.start_at} - {interval.end_at}
                              </span>
                            ) : (
                              <>
                                <a
                                  href="#"
                                  onClick={(clickEvent) => {
                                    clickEvent.preventDefault()
                                    setError(null)
                                    trackEvent('Schedule Interval Modal Opened', { is_new: false })
                                    setModal({ day, id: index, startAt: interval.start_at, endAt: interval.end_at })
                                  }}
                                >
                                  {interval.start_at} - {interval.end_at}
                                </a>
                                <a
                                  href="#"
                                  onClick={(clickEvent) => {
                                    clickEvent.preventDefault()
                                    void handleDelete(day, index)
                                  }}
                                >
                                  <i className="fa fa-trash" style={{ color: '#FF1744', marginLeft: 4 }} />
                                </a>
                              </>
                            )}
                          </li>
                        ))
                      )}
                    </ul>
                  </div>
                </div>
              )
            })}
          </div>
          <div className="col-xs-12 col-sm-3 col-sm-offset-3">
            <ButtonAnchor
              href={`/health_centers/${healthCenter.id}/professionals`}
              variant="default"
              block
              onClick={() => trackEvent('Settings Opened', { section: 'accounts', source: 'professional_calendar' }, { transport: 'sendBeacon' })}
            >
              Volver al listado
            </ButtonAnchor>
          </div>
          <div className="col-xs-12 col-sm-3">
            <ButtonAnchor href={`/health_centers/${healthCenter.id}/box_calendars`} block>
              Ir a Agenda
            </ButtonAnchor>
          </div>
          <div className="row">&nbsp;</div>
        </div>
      </div>

      <div className={styles.schedulesForm}>
        <Modal
          open={modal !== null}
          onClose={() => setModal(null)}
          id="modal_appointment"
          title={
            modal && (
              <>
                {modal.id === null ? 'Disponibilidad: ' : 'Editar horario '}
                <span className={styles.day}>{DAY_LABELS[modal.day]}</span>
              </>
            )
          }
        >
          {modal && (
            <div className="row">
              <form
                className={styles.formSchedule}
                onSubmit={(submitEvent) => {
                  submitEvent.preventDefault()
                  void handleSave()
                }}
              >
                <div className={`col-xs-12 col-sm-6 ${styles.inputHrs}`}>
                  <TextField
                    label="Desde las:"
                    labelStyle="static"
                    type="time"
                    step={900}
                    controlClassName="start-time required"
                    value={modal.startAt}
                    onChange={(value) => setModal({ ...modal, startAt: value })}
                  />
                  <span className={styles.hrs}>Horas.</span>
                </div>
                <div className={`col-xs-12 col-sm-6 ${styles.inputHrs}`}>
                  <TextField
                    label="Hasta las:"
                    labelStyle="static"
                    type="time"
                    step={900}
                    controlClassName="end-time required"
                    value={modal.endAt}
                    onChange={(value) => setModal({ ...modal, endAt: value })}
                  />
                  <span className={styles.hrs}>Horas.</span>
                </div>
                <div className={`col-xs-12 text-center ${styles.saveRow}`}>
                  <SubmitButton label="Guardar" disabled={submitting} />
                </div>
              </form>
            </div>
          )}
        </Modal>
      </div>
    </>
  )
}
